import React, { useState, useRef, useEffect, useContext, useCallback } from "react";

import { useHistory } from "react-router-dom";
import { format } from "date-fns";
import { SocketContext } from "../../context/Socket/SocketContext";

import useSound from "use-sound";

import Popover from "@material-ui/core/Popover";
import IconButton from "@material-ui/core/IconButton";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import Divider from "@material-ui/core/Divider";
import { makeStyles } from "@material-ui/core/styles";
import Badge from "@material-ui/core/Badge";
import Box from "@material-ui/core/Box";
import Typography from "@material-ui/core/Typography";
import ChatIcon from "@material-ui/icons/Chat";
import { toast } from "react-toastify";

import TicketListItem from "../TicketListItemCustom";
import useTickets from "../../hooks/useTickets";
import alertSound from "../../assets/sound.mp3";
import { AuthContext } from "../../context/Auth/AuthContext";
import { i18n } from "../../translate/i18n";
import pushNotifications from "../../services/pushNotifications";
import { canShowTicketNotification } from "../../utils/ticketNotificationVisibility";

const PUSH_AUTO_PROMPT_KEY = "neozapp:push:auto-prompt:v2";
const NOTIFICATION_EVENT_TTL_MS = 15000;
const NOTIFICATION_EVENT_WINDOW_MS = 5000;
const CARD_NOTIFICATION_EVENT_TTL_MS = 5 * 60 * 1000;
const CARD_NOTIFICATION_MAX_EVENTS = 200;

const useStyles = makeStyles(theme => ({
	tabContainer: {
		overflowY: "auto",
		maxHeight: 350,
		...theme.neoUI.scrollbar.default,
		padding: 0,
		flex: 1,
	},
	popoverPaper: {
		width: 380,
		maxHeight: 480,
		overflow: "hidden",
		borderRadius: theme.spacing(1),
		boxShadow: theme.shadows[2],
		display: "flex",
		flexDirection: "column",
		[theme.breakpoints.down("sm")]: {
			width: 270,
		},
	},
	noShadow: {
		boxShadow: "none !important",
	},
	badge: {
		"& .MuiBadge-badge": {
			backgroundColor: "#f44336 !important",
			color: "white",
			fontWeight: 700,
			boxShadow: "0 2px 4px rgba(244, 67, 54, 0.4)",
			borderRadius: 8,
			fontSize: "0.65rem",
			height: 16,
			minWidth: 16,
			padding: "0 3px",
			right: 3,
			top: 3,
		},
	},
	header: {
		padding: theme.spacing(2),
		backgroundColor: theme.palette.background.paper,
		borderBottom: `1px solid ${theme.palette.divider}`,
	},
	headerTitle: {
		fontWeight: 600,
		fontSize: "1rem",
		display: "flex",
		alignItems: "center",
		gap: theme.spacing(1),
	},
	footer: {
		padding: theme.spacing(1.5),
		borderTop: `1px solid ${theme.palette.divider}`,
		textAlign: "center",
	},
	viewAll: {
		fontSize: "0.875rem",
		color: theme.palette.primary.main,
		cursor: "pointer",
		"&:hover": { textDecoration: "underline" },
	},
}));

const NotificationsPopOver = (volume) => {
	const TriggerIcon = volume.icon || ChatIcon;
	const classes = useStyles();

	const history = useHistory();
	const { user } = useContext(AuthContext);
	const ticketIdUrl = +history.location.pathname.split("/")[2];
	const ticketIdRef = useRef(ticketIdUrl);
	const anchorEl = useRef();
	const [isOpen, setIsOpen] = useState(false);
	const [notifications, setNotifications] = useState([]);
	const [pushState, setPushState] = useState({
		supported: true,
		permission: "default",
		subscribed: false,
		loading: false,
	});

	const [showPendingTickets, setShowPendingTickets] = useState(false);
	const canShowPendingTickets =
		user?.profile === "admin" ||
		user?.viewPendingTickets === "enabled" ||
		user?.allTicket === "enabled";

	const [, setDesktopNotifications] = useState([]);

	const { tickets } = useTickets({ withUnreadMessages: "true" });

	const [play] = useSound(alertSound, volume);
	const soundAlertRef = useRef();
	const pushStateRef = useRef(pushState);
	const handledEventsRef = useRef(new Map());

	const historyRef = useRef(history);

	const socketManager = useContext(SocketContext);

	const removeNotification = useCallback((ticketId) => {
		setNotifications(prevState =>
			prevState.filter(ticket => String(ticket.id) !== String(ticketId))
		);

		setDesktopNotifications(prevState => {
			const notificationIndex = prevState.findIndex(
				notification => notification.tag === String(ticketId)
			);
			if (notificationIndex !== -1) {
				prevState[notificationIndex].close();
				return prevState.filter((_, index) => index !== notificationIndex);
			}
			return prevState;
		});
	}, []);

	const upsertNotification = useCallback((ticket) => {
		setNotifications(prevState => {
			const ticketIndex = prevState.findIndex(
				currentTicket => String(currentTicket.id) === String(ticket.id)
			);
			if (ticketIndex !== -1) {
				const nextState = [...prevState];
				nextState[ticketIndex] = ticket;
				return nextState;
			}
			return [ticket, ...prevState];
		});
	}, []);

	const applyTicketNotificationUpdate = useCallback((ticket) => {
		if (!ticket?.id) {
			return;
		}

		if (canShowTicketNotification(ticket, user, showPendingTickets)) {
			upsertNotification(ticket);
		} else {
			removeNotification(ticket.id);
		}
	}, [removeNotification, showPendingTickets, upsertNotification, user]);

	const getEventTimestamp = useCallback((value) => {
		if (!value) {
			return Date.now();
		}

		const parsedDate = new Date(value).getTime();
		if (Number.isNaN(parsedDate)) {
			return Date.now();
		}

		return parsedDate;
	}, []);

	const buildNotificationEventKey = useCallback((payload = {}) => {
		const messageId =
			payload?.messageId ??
			payload?.message?.id ??
			payload?.newMessage?.id ??
			null;

		if (messageId) {
			return `msg:${String(messageId)}`;
		}

		const ticketId =
			payload?.ticketId ??
			payload?.ticket?.id ??
			payload?.message?.ticketId ??
			payload?.chat?.id ??
			null;
		const body =
			payload?.body ??
			payload?.message?.body ??
			payload?.newMessage?.message ??
			payload?.title ??
			"";
		const timestamp =
			payload?.timestamp ??
			payload?.message?.createdAt ??
			payload?.newMessage?.createdAt ??
			Date.now();
		const timeWindow = Math.floor(getEventTimestamp(timestamp) / NOTIFICATION_EVENT_WINDOW_MS);

		return `fallback:${ticketId || "unknown"}:${String(body).slice(0, 40)}:${timeWindow}`;
	}, [getEventTimestamp]);

	const cleanHandledEvents = useCallback((now = Date.now()) => {
		for (const [key, timestamp] of handledEventsRef.current.entries()) {
			if (now - timestamp > NOTIFICATION_EVENT_TTL_MS) {
				handledEventsRef.current.delete(key);
			}
		}
	}, []);

	const hasHandledEvent = useCallback((payload = {}) => {
		const key = buildNotificationEventKey(payload);
		const now = Date.now();
		cleanHandledEvents(now);
		const lastSeenAt = handledEventsRef.current.get(key);
		return typeof lastSeenAt === "number" && now - lastSeenAt <= NOTIFICATION_EVENT_TTL_MS;
	}, [buildNotificationEventKey, cleanHandledEvents]);

	const registerHandledEvent = useCallback((payload = {}) => {
		const key = buildNotificationEventKey(payload);
		cleanHandledEvents();
		handledEventsRef.current.set(key, Date.now());
	}, [buildNotificationEventKey, cleanHandledEvents]);

	useEffect(() => {
		setShowPendingTickets(Boolean(canShowPendingTickets));
	}, [canShowPendingTickets]);

	useEffect(() => {
		pushStateRef.current = pushState;
	}, [pushState]);

	useEffect(() => {
		soundAlertRef.current = play;

		if (!("Notification" in window)) {
			console.log("This browser doesn't support notifications");
		} else if (Notification.permission === "default") {
			// Permission prompt must be user-initiated to avoid repeated browser blocking.
			console.log("Notification permission is pending user consent.");
		}
	}, [play]);

	const refreshPushState = useCallback(async () => {
		const supported = pushNotifications.isSupported();
		if (!supported) {
			setPushState(prevState => ({
				...prevState,
				supported: false,
				permission: "unsupported",
				subscribed: false,
				loading: false,
			}));
			return;
		}

		const permission = pushNotifications.getPermissionState();
		let subscribed = false;

		if (permission === "granted") {
			try {
				const registration = await navigator.serviceWorker.ready;
				const activeSubscription = await registration.pushManager.getSubscription();
				subscribed = Boolean(activeSubscription);
			} catch (error) {
				console.warn("Não foi possível verificar subscription push:", error);
			}
		}

		setPushState(prevState => ({
			...prevState,
			supported: true,
			permission,
			subscribed,
			loading: false,
		}));
	}, []);

	useEffect(() => {
		refreshPushState();
	}, [refreshPushState]);

	useEffect(() => {
		if (typeof window === "undefined") {
			return undefined;
		}

		if (!pushNotifications.isSupported()) {
			return undefined;
		}

		if (Notification.permission !== "default") {
			return undefined;
		}

		if (window.localStorage.getItem(PUSH_AUTO_PROMPT_KEY) === "done") {
			return undefined;
		}

		let cancelled = false;
		let interactionRequested = false;

		const requestNativePermission = async () => {
			if (cancelled || Notification.permission !== "default") {
				return;
			}

			try {
				const permission = await Notification.requestPermission();
				if (permission === "granted") {
					await pushNotifications.ensureSubscription({
						requestPermission: false
					});
					window.localStorage.setItem(PUSH_AUTO_PROMPT_KEY, "done");
				} else if (permission === "denied") {
					window.localStorage.setItem(PUSH_AUTO_PROMPT_KEY, "done");
				}
			} catch (error) {
				console.warn("Falha ao solicitar permissão push automaticamente:", error);
			} finally {
				if (!cancelled) {
					await refreshPushState();
				}
			}
		};

		// Tenta imediatamente para navegadores que permitem prompt sem gesto.
		requestNativePermission();

		// Fallback para plataformas que exigem interação do usuário (desktop/mobile).
		const requestOnInteraction = () => {
			if (interactionRequested || cancelled) {
				return;
			}
			interactionRequested = true;
			requestNativePermission();
		};

		window.addEventListener("pointerdown", requestOnInteraction, { once: true });
		window.addEventListener("keydown", requestOnInteraction, { once: true });

		return () => {
			cancelled = true;
			window.removeEventListener("pointerdown", requestOnInteraction);
			window.removeEventListener("keydown", requestOnInteraction);
		};
	}, [refreshPushState]);

	useEffect(() => {
		if (isOpen) {
			refreshPushState();
		}
	}, [isOpen, refreshPushState]);

	useEffect(() => {
		const handleForegroundPush = (event) => {
			const detail = event?.detail || {};
			const payload = detail.payload || detail;
			const payloadTicketId = Number(payload?.ticketId);
			const isCurrentTicketVisible =
				document.visibilityState === "visible" &&
				Number.isFinite(payloadTicketId) &&
				payloadTicketId === Number(ticketIdRef.current);

			if (isCurrentTicketVisible) {
				registerHandledEvent(payload);
				return;
			}

			if (hasHandledEvent(payload)) {
				return;
			}
			registerHandledEvent(payload);

			if (payload?.title) {
				toast.info(payload.title, { autoClose: 2500 });
			}
		};

		window.addEventListener("neozapp:push-foreground", handleForegroundPush);

		return () => {
			window.removeEventListener("neozapp:push-foreground", handleForegroundPush);
		};
	}, [hasHandledEvent, registerHandledEvent]);

	useEffect(() => {
		const newNotifications = tickets.filter(ticket =>
			canShowTicketNotification(ticket, user, showPendingTickets)
		);

		setNotifications(newNotifications);
	}, [tickets, user, showPendingTickets]);

	useEffect(() => {
		const handlePatchedTicketEvent = (event) => {
			applyTicketNotificationUpdate(event.detail?.ticket);
		};

		window.addEventListener("ticket:patched", handlePatchedTicketEvent);

		return () => {
			window.removeEventListener("ticket:patched", handlePatchedTicketEvent);
		};
	}, [applyTicketNotificationUpdate]);

	useEffect(() => {
		ticketIdRef.current = ticketIdUrl;
	}, [ticketIdUrl]);

	useEffect(() => {
		const companyId = user?.companyId;
		if (!companyId || !user?.id) return undefined;

		const socket = socketManager.getSocket(companyId);
		const eventName = `company-${companyId}-card-notification`;
		const handledEvents = new Map();
		const toastIds = new Set();
		const handleCardNotification = payload => {
			if (payload?.type !== "card_flow_notification") return;
			const cardId = Number(payload.cardId);
			const boardId = Number(payload.kanbanBoardId);
			if (!Number.isSafeInteger(cardId) || cardId <= 0 ||
				!Number.isSafeInteger(boardId) || boardId <= 0) return;

			const title = typeof payload.title === "string" ? payload.title.trim() : "";
			const body = typeof payload.body === "string" ? payload.body.trim() : "";
			if (!title && !body) return;

			const now = Date.now();
			for (const [key, expiresAt] of handledEvents) {
				if (expiresAt <= now) handledEvents.delete(key);
			}
			const eventKey = typeof payload.eventId === "string" && payload.eventId
				? `event:${payload.eventId}`
				: JSON.stringify([cardId, boardId, payload.flowId, payload.nodeId, payload.timestamp, title, body]);
			if (handledEvents.has(eventKey)) return;
			if (handledEvents.size >= CARD_NOTIFICATION_MAX_EVENTS) {
				handledEvents.delete(handledEvents.keys().next().value);
			}
			handledEvents.set(eventKey, now + CARD_NOTIFICATION_EVENT_TTL_MS);

			const toastId = toast.info([title, body].filter(Boolean).join("\n"), {
				autoClose: payload.priority === "high" ? 8000 : 5000,
				onClose: () => toastIds.delete(toastId),
				onClick: () => historyRef.current.push(`/kanban?boardId=${boardId}&cardId=${cardId}&openCard=1`)
			});
			if ((typeof toastId === "string" && toastId) ||
				(typeof toastId === "number" && Number.isFinite(toastId))) toastIds.add(toastId);
			if (payload.sound === true && soundAlertRef.current) soundAlertRef.current();
		};

		// Recipients already belong to authenticated user rooms; no additional join.
		socket.on(eventName, handleCardNotification);
		return () => {
			socket.off(eventName, handleCardNotification);
			socket.disconnect();
			for (const toastId of toastIds) toast.dismiss(toastId);
			toastIds.clear();
		};
	}, [socketManager, user?.companyId, user?.id]);

	useEffect(() => {
		const socket = socketManager.getSocket(user.companyId);

		socket.on("ready", () => socket.emit("joinNotification"));

		socket.on(`company-${user.companyId}-ticket`, data => {
			if (data.action === "updateUnread" || data.action === "delete") {
				removeNotification(data.ticketId);
				return;
			}

			if (data.action === "update" && data.ticket) {
				applyTicketNotificationUpdate(data.ticket);
			}
		});

		socket.on(`company-${user.companyId}-appMessage`, data => {
			const canNotifyPending = Boolean(canShowPendingTickets);
			if (
				data.action === "create" && !data.message.fromMe &&
				(!data.message.read || data.ticket.status === "pending") &&
				(data.ticket.userId === user?.id || !data.ticket.userId) &&
				(user?.queues?.some(queue => (queue.id === data.ticket.queueId)) || !data.ticket.queueId)
			) {
				if (data.ticket.status === "pending" && !canNotifyPending) {
					return;
				}
				setNotifications(prevState => {
					const ticketIndex = prevState.findIndex(t => t.id === data.ticket.id);
					if (ticketIndex !== -1) {
						prevState[ticketIndex] = data.ticket;
						return [...prevState];
					}
					return [data.ticket, ...prevState];
				});

				const shouldNotNotificate =
					(data.message.ticketId === ticketIdRef.current &&
						document.visibilityState === "visible") ||
					(data.ticket.userId && data.ticket.userId !== user?.id) ||
					data.ticket.isGroup;

				if (shouldNotNotificate) return;

				if (hasHandledEvent(data)) {
					return;
				}

				const currentPushState = pushStateRef.current;
				const canDeferToPushInBackground =
					document.visibilityState !== "visible" &&
					currentPushState?.supported &&
					currentPushState?.permission === "granted" &&
					currentPushState?.subscribed;

				if (canDeferToPushInBackground) {
					return;
				}

				handleNotifications(data);
			}
		});

		return () => {
			socket.disconnect();
		};
	}, [user, canShowPendingTickets, socketManager, hasHandledEvent, removeNotification, applyTicketNotificationUpdate]);

	const handleNotifications = data => {
		if (hasHandledEvent(data)) {
			return;
		}

		registerHandledEvent(data);

		const { message, contact, ticket } = data;
		const isPageVisible = document.visibilityState === "visible";
		const canUseDesktopNotifications =
			"Notification" in window && Notification.permission === "granted";

		if (isPageVisible) {
			if (soundAlertRef.current) {
				soundAlertRef.current();
			}
			return;
		}

		if (!canUseDesktopNotifications) {
			if (soundAlertRef.current) {
				soundAlertRef.current();
			}
			return;
		}

		// Normalize body for special message types (VCARD, location, etc.)
		const normalizeNotificationBody = (body, mediaType) => {
			if (!body || typeof body !== "string") return body || "Você recebeu uma nova mensagem";
			const trimmed = body.trim();
			const upper = trimmed.toUpperCase();

			// Check for VCARD content
			if (upper.includes("BEGIN:VCARD")) {
				return "📲 Contato";
			}
			// Check for VCARD JSON array
			if (trimmed.startsWith("[") && /["']vcard["']/i.test(trimmed)) {
				return "📲 Contato";
			}
			// Check mediaType for contact types
			if (mediaType === "contactMessage" || mediaType === "contactsArrayMessage") {
				return "📲 Contato";
			}
			// Check for location
			if (mediaType === "locationMessage" || mediaType === "liveLocationMessage") {
				return "📍 Localização";
			}

			return body;
		};

		const normalizedBody = normalizeNotificationBody(message.body, message.mediaType);

		const options = {
			body: `${normalizedBody} - ${format(new Date(), "HH:mm")}`,
			icon: contact.urlPicture,
			tag: ticket.id,
			renotify: true,
		};

		const notification = new Notification(
			`${i18n.t("tickets.notification.message")} ${contact.name}`,
			options
		);

		notification.onclick = e => {
			e.preventDefault();
			window.focus();
			const buildTicketPath = (currentTicket) => {
				if (!currentTicket) return "/tickets";
				if (currentTicket.id) {
					return `/tickets/${currentTicket.id}`;
				}
				if (currentTicket.uuid) {
					return `/tickets/${currentTicket.uuid}`;
				}
				return "/tickets";
			};

			const path = buildTicketPath(ticket);
			historyRef.current.push(path, { ticket });
			// handleChangeTab(null, ticket.isGroup? "group" : "open");
		};

		setDesktopNotifications(prevState => {
			const notfiticationIndex = prevState.findIndex(
				n => n.tag === notification.tag
			);
			if (notfiticationIndex !== -1) {
				prevState[notfiticationIndex] = notification;
				return [...prevState];
			}
			return [notification, ...prevState];
		});
	};

	const handleClick = () => {
		setIsOpen(prevState => !prevState);
	};

	const handleClickAway = () => {
		setIsOpen(false);
	};

	const pushStatusText = (() => {
		if (!pushState.supported) {
			return "Push indisponível neste navegador/dispositivo.";
		}
		if (pushState.permission === "denied") {
			return "Permissão bloqueada. Libere nas configurações do sistema/navegador.";
		}
		return null;
	})();

	const NotificationTicket = ({ children }) => {
		return <div onClick={handleClickAway}>{children}</div>;
	};

	return (
		<>
			<IconButton
				onClick={handleClick}
				ref={anchorEl}
				aria-label={volume.icon ? "Notificações" : "Open Notifications"}
				color="inherit"
				style={{
					color: "white",
					width: 34,
					height: 34,
					padding: 6,
					borderRadius: "50%",
					margin: "0 3.2px"
				}}
			>
				<Badge overlap="rectangular" badgeContent={notifications.length} color="secondary" className={classes.badge}>
					<TriggerIcon />
				</Badge>
			</IconButton>
				<Popover
				disableScrollLock
				open={isOpen}
				anchorEl={anchorEl.current}
				anchorOrigin={{
					vertical: "bottom",
					horizontal: "right",
				}}
				transformOrigin={{
					vertical: "top",
					horizontal: "right",
				}}
				classes={{ paper: classes.popoverPaper }}
				onClose={handleClickAway}
				>
					<Box className={classes.header}>
						<Typography className={classes.headerTitle}>
							<ChatIcon fontSize="small" />
							{i18n.t("tickets.title") || "Tickets"}
						</Typography>
					</Box>

					<List dense className={classes.tabContainer}>
						{pushStatusText && (
							<>
								<ListItem>
									<ListItemText
										primary="Push Notifications"
										secondary={pushStatusText}
									/>
								</ListItem>
								<Divider component="li" />
							</>
						)}
						{notifications.length === 0 ? (
							<ListItem>
								<ListItemText>{i18n.t("notifications.noTickets")}</ListItemText>
							</ListItem>
						) : (
							notifications.map(ticket => (
								<NotificationTicket key={ticket.id}>
									<TicketListItem ticket={ticket} />
								</NotificationTicket>
							))
						)}
					</List>

					{notifications.length > 0 && (
						<Box className={classes.footer}>
							<Typography
								className={classes.viewAll}
								onClick={() => {
									handleClickAway();
									history.push("/tickets");
								}}
							>
								{i18n.t("notifications.viewAll") || "Ver todos os tickets"}
							</Typography>
						</Box>
					)}
			</Popover>
		</>
	);
};

export default NotificationsPopOver;
